import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router-dom'
import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import {
  deactivateCompany,
  getCompany,
  getCompanyAssetSignedUrl,
  nameDuplicateWarning,
  softDeleteCompany,
  updateCompany,
  uploadCompanyAsset,
  type CompanyListResult,
} from '@/api/companies'
import { listUsers } from '@/api/users'
import { listOrders } from '@/api/orders'
import { getRatesBase } from '@/api/tariffs'
import { DetailHeaderSkeleton, SectionSkeleton } from '@/components/ui/Skeleton'
import { emptyTableCellClassName } from '@/constants/ui'
import { CompanyStatusBadge } from '@/components/companies/CompanyStatusBadge'
import { Breadcrumbs } from '@/components/layout/Breadcrumbs'
import { Button } from '@/components/ui/Button'
import { AlertDialog, ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { Select } from '@/components/ui/Select'
import { PillToggle } from '@/components/ui/PillToggle'
import { Input } from '@/components/ui/Input'
import { DocumentUploader } from '@/components/ui/DocumentUploader'
import { CompanyDkpTemplateSection } from '@/components/company/CompanyDkpTemplateSection'
import { CompanyDocumentsSection } from '@/components/company/CompanyDocumentsSection'
import { useAuthStore } from '@/store/authStore'
import type { Company } from '@/types/company'
import type { CompanyType, OrderStatus } from '@/types/enums'
import { statusLabelRu } from '@/utils/status-transitions'
import { formatPhoneDisplay, formatPhoneInput } from '@/utils/phone'
import { formatRubAmountCell } from '@/utils/money-input'
import {
  digitsOnly,
  validateBankAccount,
  validateBik,
  validateCorrAccount,
  validateInn,
  validateKpp,
  validateOgrn,
} from '@/utils/companyValidators'

function ReadOnlyField({
  label,
  children,
  borderRight,
}: {
  label: string
  children: ReactNode
  borderRight?: boolean
}) {
  return (
    <div
      className={
        borderRight
          ? 'min-w-0 min-[640px]:border-r min-[640px]:border-solid min-[640px]:border-[rgba(255,255,255,0.08)] min-[640px]:pr-4'
          : 'min-w-0 min-[640px]:pl-4'
      }
    >
      <div className="stryzh-input-label">{label}</div>
      <div className="font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-base)] text-[color:var(--stryzh-color-text-primary)]">
        {children}
      </div>
    </div>
  )
}

export function CompanyDetailPage() {
  const { t } = useTranslation()
  const { id = '' } = useParams()
  const nav = useNavigate()
  const qc = useQueryClient()
  const { data: company, isLoading: companyLoading } = useQuery({
    queryKey: ['company', id],
    queryFn: () => getCompany(id),
    enabled: !!id,
    /** Берём строку из уже загруженного списка, чтобы не мигало «Загрузка…». */
    initialData: () => {
      if (!id) return undefined
      // Защищаемся `Array.isArray(v.items)` от случая, когда в кэше под
      // префиксом ['companies', ...] окажется запрос с другой формой данных
      // (см. тот же фикс в OrderDetailPage). Иначе `v?.items.find` падает.
      const lists = qc.getQueriesData<CompanyListResult>({ queryKey: ['companies'] })
      for (const [, v] of lists) {
        if (!v || !Array.isArray(v.items)) continue
        const hit = v.items.find((c) => c.id === id)
        if (hit) return hit
      }
      return undefined
    },
    initialDataUpdatedAt: () =>
      qc.getQueryState(['companies'])?.dataUpdatedAt,
  })
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState<Partial<Company>>({})
  // Текстовое значение для number-input «Коэффициент» в режиме редактирования.
  // Храним строкой, чтобы поле можно было стереть и набрать заново.
  const [k3Text, setK3Text] = useState<string>('1')
  const [confirmDeactivateOpen, setConfirmDeactivateOpen] = useState(false)
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  // Хук должен быть до условных `return` ниже — иначе при первом рендере
  // (companyLoading=true) хук не вызовется, а после прихода данных вызовется,
  // и React упадёт #310 «Rendered more hooks than during the previous render».
  const currentRole = useAuthStore((s) => s.profile?.role)

  const k3Parsed = Number(k3Text)
  // K3 по §6.4 TARIFFS_SPEC.md применим только к заказчику; у исполнителя
  // поле скрыто и валидировать его не нужно.
  const k3Applicable =
    (editing ? form.type : company?.type) === 'customer'
  const k3Valid =
    !editing ||
    !k3Applicable ||
    (k3Text.trim() !== '' && Number.isFinite(k3Parsed) && k3Parsed > 0)

  // Сотрудники компании — отдельным запросом по FK profiles.company_id.
  const { data: usersData } = useQuery({
    queryKey: ['company-users', id],
    queryFn: () =>
      listUsers({
        page: 1,
        pageSize: 200,
        companyId: id,
        role: 'all',
        includeInactive: true,
      }),
    enabled: !!id,
  })
  const users = usersData?.items ?? []

  // Заказы компании — через listOrders({ companyId }); подхватит customer и contractor.
  const { data: ordersData } = useQuery({
    queryKey: ['company-orders', id],
    queryFn: () => listOrders({ page: 1, pageSize: 100, companyId: id }),
    enabled: !!id,
  })
  const orders = ordersData?.items ?? []

  // Базовые тарифы нужны для «справочно» блока в карточке компании.
  // Для заказчика: base_customer.{service} × K3 (без K1/K2 — они зависят
  // от заказа). Для исполнителя: base_inspector.{service} без K3 и без
  // K1/K2 — К3 по §6.4 TARIFFS_SPEC.md применим только к стороне заказчика.
  const { data: ratesBase } = useQuery({
    queryKey: ['rates-base'],
    queryFn: () => getRatesBase(),
  })

  const warnDup =
    editing && form.name?.trim()
      ? nameDuplicateWarning(form.name, id)
      : false

  const mutSave = useMutation({
    mutationFn: async () => {
      if (!company) return
      if (!k3Valid) return
      const nextType = (form.type as CompanyType) ?? company.type
      // Required-валидация по ТЗ. Кидаем Error → onError выведет в AlertDialog.
      const requireField = (val: string | null | undefined, label: string) => {
        if (!val || !val.trim())
          throw new Error(t('companies.requiredField', { label }))
      }
      requireField(form.name, t('companies.field.name'))
      requireField(form.inn, t('companies.field.inn'))
      requireField(form.phone, t('companies.field.phone'))
      requireField(form.email, t('companies.field.email'))
      requireField(form.legal_address, t('companies.field.legalAddress'))
      requireField(form.kpp, t('companies.field.kpp'))
      requireField(form.ogrn, t('companies.field.ogrn'))
      requireField(form.bank_name, t('companies.field.bank'))
      requireField(form.bik, t('companies.field.bik'))
      requireField(form.bank_account, t('companies.field.bankAccount'))
      requireField(form.correspondent_account, t('companies.field.corrAccount'))
      requireField(form.signatory_name, t('companies.field.signatoryName'))
      requireField(form.signatory_position, t('companies.field.signatoryPosition'))
      requireField(form.signatory_basis, t('companies.field.signatoryBasis'))
      // Формат реквизитов (длина / только цифры). Без этой проверки save
      // пропускает мусор в БД, хотя визуально ошибка под полем показывается.
      const formatError =
        validateInn(form.inn ?? '') ??
        validateKpp(form.kpp ?? '') ??
        validateOgrn(form.ogrn ?? '') ??
        validateBik(form.bik ?? '') ??
        validateBankAccount(form.bank_account ?? '') ??
        validateCorrAccount(form.correspondent_account ?? '')
      if (formatError) throw new Error(formatError)
      await updateCompany(company.id, {
        name: form.name!.trim(),
        type: nextType,
        inn: form.inn ?? null,
        phone: form.phone ?? null,
        legal_address: form.legal_address ?? null,
        email: form.email ?? null,
        k3: nextType === 'customer' ? k3Parsed : 1.0,
        is_active: form.is_active ?? company.is_active,
        signatory_name: form.signatory_name ?? company.signatory_name,
        signatory_position: form.signatory_position ?? company.signatory_position,
        signatory_basis: form.signatory_basis ?? company.signatory_basis,
        kpp: form.kpp ?? company.kpp,
        ogrn: form.ogrn ?? company.ogrn,
        bank_account: form.bank_account ?? company.bank_account,
        bank_name: form.bank_name ?? company.bank_name,
        bik: form.bik ?? company.bik,
        correspondent_account:
          form.correspondent_account ?? company.correspondent_account,
        facsimile_url: form.facsimile_url ?? company.facsimile_url,
        seal_url: form.seal_url ?? company.seal_url,
      })
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['company', id] })
      void qc.invalidateQueries({ queryKey: ['companies'] })
      setEditing(false)
    },
    onError: (e: Error) => setErrorMessage(e.message),
  })

  const mutDeactivate = useMutation({
    mutationFn: () => deactivateCompany(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['company', id] })
      void qc.invalidateQueries({ queryKey: ['companies'] })
    },
    onError: (e: Error) => setErrorMessage(e.message),
  })

  const mutSoftDelete = useMutation({
    mutationFn: () => softDeleteCompany(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['company', id] })
      void qc.invalidateQueries({ queryKey: ['companies'] })
    },
    onError: (e: Error) => setErrorMessage(e.message),
  })

  if (companyLoading && !company) {
    return (
      <div className="w-full max-w-none px-6 py-4 lg:px-8">
        <DetailHeaderSkeleton />
        <div className="flex flex-col gap-6 min-[768px]:flex-row">
          <div className="flex min-w-0 flex-col gap-4 min-[768px]:flex-1">
            <SectionSkeleton lines={6} />
            <SectionSkeleton lines={2} />
          </div>
          <SectionSkeleton lines={3} className="min-[768px]:flex-1" />
        </div>
      </div>
    )
  }

  if (!company) {
    return (
      <p className="p-8 text-[var(--stryzh-color-text-secondary)]">{t('companies.notFound')}</p>
    )
  }

  function startEdit() {
    setForm({ ...company })
    setK3Text(String(company?.k3 ?? 1))
    setEditing(true)
  }

  const display: Company = editing ? { ...company, ...form } : company
  const typeLocked = users.length > 0

  const card =
    'rounded-xl border border-[var(--stryzh-color-border-default)] bg-[var(--stryzh-color-surface)] p-4'

  return (
    <div className="w-full max-w-none px-6 py-4 lg:px-8">
      <AlertDialog
        open={errorMessage != null}
        title={t('companies.errorTitle')}
        message={errorMessage ?? ''}
        onClose={() => setErrorMessage(null)}
      />
      <ConfirmDialog
        open={confirmDeactivateOpen}
        title={t('companies.deactivateTitle')}
        cancelLabel={t('common.cancel')}
        confirmLabel={t('companies.deactivateConfirm')}
        confirmVariant="danger"
        onCancel={() => setConfirmDeactivateOpen(false)}
        onConfirm={() => {
          setConfirmDeactivateOpen(false)
          mutDeactivate.mutate()
        }}
      >
        {t('companies.deactivateBody')}
      </ConfirmDialog>
      <ConfirmDialog
        open={confirmDeleteOpen}
        title={t('companies.deleteTitle')}
        cancelLabel={t('common.cancel')}
        confirmLabel={t('companies.delete')}
        confirmVariant="danger"
        onCancel={() => setConfirmDeleteOpen(false)}
        onConfirm={() => {
          setConfirmDeleteOpen(false)
          mutSoftDelete.mutate()
        }}
      >
        {t('companies.deleteBody')}
      </ConfirmDialog>
      <Breadcrumbs
        items={[{ label: t('companies.title'), to: '/companies' }, { label: company.name }]}
      />
      <header className="mb-6 flex flex-col gap-2">
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3">
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-2">
            <h1 className="min-w-0 break-words font-[family-name:var(--stryzh-font-family-space-grotesk)] text-[length:var(--stryzh-font-size-hero)] font-semibold tracking-[var(--stryzh-letter-spacing-space-grotesk)] text-[color:var(--stryzh-color-text-primary)]">
              {company.name}
            </h1>
            <CompanyStatusBadge isActive={display.is_active} />
          </div>
          {!editing ? (
            <div className="flex min-w-0 max-w-full flex-wrap items-center justify-end gap-2">
            <Button variant="outline" onClick={startEdit}>
              {t('companies.edit')}
            </Button>
            {display.deleted_at ? null : display.is_active ? (
              <Button
                variant="danger"
                disabled={mutDeactivate.isPending}
                onClick={() => setConfirmDeactivateOpen(true)}
              >
                {t('companies.block')}
              </Button>
            ) : (
              <Button
                variant="primary"
                onClick={() => {
                  void updateCompany(id, {
                    is_active: true,
                    status: 'active',
                    deleted_at: null,
                  }).then(() => {
                    void qc.invalidateQueries({ queryKey: ['company', id] })
                    void qc.invalidateQueries({ queryKey: ['companies'] })
                  })
                }}
              >
                {t('companies.unblock')}
              </Button>
            )}
            {!display.deleted_at ? (
              <Button
                variant="danger"
                disabled={mutSoftDelete.isPending}
                onClick={() => setConfirmDeleteOpen(true)}
              >
                {t('companies.delete')}
              </Button>
            ) : null}
            </div>
          ) : (
            <div className="flex min-w-0 max-w-full flex-wrap items-center justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setEditing(false)
                  setForm({})
                }}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="primary"
                disabled={mutSave.isPending || !!warnDup || !k3Valid}
                onClick={() => mutSave.mutate()}
              >
                {t('common.save')}
              </Button>
            </div>
          )}
        </div>
      </header>

      <div className="flex flex-col gap-6 min-[768px]:min-h-0 min-[768px]:flex-row min-[768px]:items-stretch">
        <div className="flex min-w-0 min-[768px]:min-h-0 min-[768px]:flex-1 flex-col gap-4">
          <section className={card}>
            <h2 className="stryzh-heading-card mb-4">{t('companies.section.main')}</h2>
            {editing ? (
              <div className="grid gap-3">
                <Input
                  label={t('companies.field.name')}
                  required
                  value={display.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
                {warnDup ? (
                  <p className="text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-accent-orange)]">
                    {t('companies.dupExists')}
                  </p>
                ) : null}
                <label className="flex flex-col gap-1">
                  <span className="stryzh-input-label">{t('companies.field.type')}</span>
                  <Select
                    disabled={typeLocked}
                    value={display.type}
                    onChange={(v) => setForm({ ...form, type: v as CompanyType })}
                    options={[
                      { value: 'customer', label: t('companyType.customer') },
                      { value: 'contractor', label: t('companyType.contractor') },
                    ]}
                  />
                </label>
                <div className="grid gap-3 max-[639px]:grid-cols-1 min-[640px]:grid-cols-2 min-[640px]:[grid-template-columns:1fr_1fr]">
                  <div className="min-[640px]:border-r min-[640px]:border-solid min-[640px]:border-[rgba(255,255,255,0.08)] min-[640px]:pr-4">
                    <Input
                      label={t('companies.field.inn')}
                      required
                      inputMode="numeric"
                      maxLength={12}
                      value={display.inn ?? ''}
                      onChange={(e) =>
                        setForm({ ...form, inn: digitsOnly(e.target.value) || null })
                      }
                      error={validateInn(display.inn ?? '') ?? undefined}
                    />
                  </div>
                  <div className="min-[640px]:pl-4">
                    <Input
                      label={t('companies.field.phone')}
                      required
                      value={display.phone ?? ''}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          phone: formatPhoneInput(display.phone ?? '', e.target.value),
                        })
                      }
                    />
                  </div>
                </div>
                <Input
                  label={t('companies.field.legalAddress')}
                  required
                  value={display.legal_address ?? ''}
                  onChange={(e) =>
                    setForm({ ...form, legal_address: e.target.value || null })
                  }
                />
                <Input
                  label={t('companies.field.email')}
                  required
                  value={display.email ?? ''}
                  onChange={(e) => setForm({ ...form, email: e.target.value || null })}
                />
                {display.type === 'customer' ? (
                  <Input
                    label={t('companies.field.coefficient')}
                    type="number"
                    step="0.01"
                    min="0.01"
                    inputMode="decimal"
                    autoComplete="off"
                    value={k3Text}
                    onChange={(e) => setK3Text(e.target.value)}
                    error={k3Valid ? undefined : t('companies.coefficientError')}
                    hint={t('companies.coefficientHint')}
                  />
                ) : null}
                <PillToggle
                  checked={display.is_active}
                  onChange={(v) => setForm({ ...form, is_active: v })}
                  label={t('companies.field.activity')}
                />
              </div>
            ) : (
              <>
                <div className="grid gap-4 max-[639px]:grid-cols-1 min-[640px]:grid-cols-2 min-[640px]:[grid-template-columns:1fr_1fr]">
                  <ReadOnlyField label={t('companies.field.type')} borderRight>
                    {display.type === 'customer'
                      ? t('companyType.customer')
                      : t('companyType.contractor')}
                  </ReadOnlyField>
                  <ReadOnlyField label={t('companies.field.inn')}>{display.inn ?? '—'}</ReadOnlyField>
                  <ReadOnlyField label={t('companies.field.phone')} borderRight>
                    {formatPhoneDisplay(display.phone)}
                  </ReadOnlyField>
                  <ReadOnlyField label={t('companies.field.address')}>{display.legal_address ?? '—'}</ReadOnlyField>
                  <ReadOnlyField label={t('companies.field.email')} borderRight>
                    {display.email ?? '—'}
                  </ReadOnlyField>
                  <div className="hidden min-[640px]:block min-[640px]:pl-4" aria-hidden />
                </div>
                {display.type === 'customer' ? (
                  <>
                    <div className="my-4 border-t border-solid border-[rgba(255,255,255,0.08)]" />
                    <div className="grid gap-4 max-[639px]:grid-cols-1 min-[640px]:grid-cols-2 min-[640px]:[grid-template-columns:1fr_1fr]">
                      <ReadOnlyField label={t('companies.field.coefficient')} borderRight>
                        {display.k3.toFixed(2)}
                      </ReadOnlyField>
                      <div className="hidden min-[640px]:block min-[640px]:pl-4" aria-hidden />
                    </div>
                    <div className="my-4 border-t border-solid border-[rgba(255,255,255,0.08)]" />
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <div className="stryzh-input-label">
                        {t('companies.tariffsCustomer')}
                      </div>
                      <div className="font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-text-secondary)]">
                        {t('companies.tariffsBaseK3')}
                      </div>
                    </div>
                    <div className="grid gap-4 max-[639px]:grid-cols-1 min-[640px]:grid-cols-2 min-[640px]:[grid-template-columns:1fr_1fr]">
                      <ReadOnlyField label={t('companies.service.trip')} borderRight>
                        {ratesBase ? `${formatRubAmountCell(Math.round(ratesBase.trip_customer * display.k3))} ₽` : '—'}
                      </ReadOnlyField>
                      <ReadOnlyField label={t('companies.service.inspection')}>
                        {ratesBase ? `${formatRubAmountCell(Math.round(ratesBase.inspection_customer * display.k3))} ₽` : '—'}
                      </ReadOnlyField>
                      <ReadOnlyField label={t('companies.service.deal')} borderRight>
                        {ratesBase ? `${formatRubAmountCell(Math.round(ratesBase.deal_customer * display.k3))} ₽` : '—'}
                      </ReadOnlyField>
                      <ReadOnlyField label={t('companies.service.delivery')}>
                        {ratesBase ? `${formatRubAmountCell(Math.round(ratesBase.delivery_customer * display.k3))} ₽` : '—'}
                      </ReadOnlyField>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="my-4 border-t border-solid border-[rgba(255,255,255,0.08)]" />
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <div className="stryzh-input-label">
                        {t('companies.tariffsInspector')}
                      </div>
                      <div className="font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-text-secondary)]">
                        {t('companies.tariffsBase')}
                      </div>
                    </div>
                    <div className="grid gap-4 max-[639px]:grid-cols-1 min-[640px]:grid-cols-2 min-[640px]:[grid-template-columns:1fr_1fr]">
                      <ReadOnlyField label={t('companies.service.trip')} borderRight>
                        {ratesBase ? `${formatRubAmountCell(Math.round(ratesBase.trip_inspector))} ₽` : '—'}
                      </ReadOnlyField>
                      <ReadOnlyField label={t('companies.service.inspection')}>
                        {ratesBase ? `${formatRubAmountCell(Math.round(ratesBase.inspection_inspector))} ₽` : '—'}
                      </ReadOnlyField>
                      <ReadOnlyField label={t('companies.service.deal')} borderRight>
                        {ratesBase ? `${formatRubAmountCell(Math.round(ratesBase.deal_inspector))} ₽` : '—'}
                      </ReadOnlyField>
                      <ReadOnlyField label={t('companies.service.delivery')}>
                        {ratesBase ? `${formatRubAmountCell(Math.round(ratesBase.delivery_inspector))} ₽` : '—'}
                      </ReadOnlyField>
                    </div>
                  </>
                )}
              </>
            )}
          </section>

          <section className={card}>
            <h2 className="stryzh-heading-card mb-4">{t('companies.section.requisites')}</h2>
            {editing ? (
              <div className="grid gap-3">
                <div className="grid gap-3 max-[639px]:grid-cols-1 min-[640px]:grid-cols-2 min-[640px]:[grid-template-columns:1fr_1fr]">
                  <Input
                    label={t('companies.field.kpp')}
                    required
                    inputMode="numeric" maxLength={9}
                    value={display.kpp ?? ''}
                    onChange={(e) =>
                      setForm({ ...form, kpp: digitsOnly(e.target.value) || null })
                    }
                    error={validateKpp(display.kpp ?? '') ?? undefined}
                  />
                  <Input
                    label={t('companies.field.ogrn')}
                    required
                    inputMode="numeric" maxLength={13}
                    value={display.ogrn ?? ''}
                    onChange={(e) =>
                      setForm({ ...form, ogrn: digitsOnly(e.target.value) || null })
                    }
                    error={validateOgrn(display.ogrn ?? '') ?? undefined}
                  />
                  <Input
                    label={t('companies.field.bank')}
                    required
                    value={display.bank_name ?? ''}
                    onChange={(e) =>
                      setForm({ ...form, bank_name: e.target.value || null })
                    }
                  />
                  <Input
                    label={t('companies.field.bik')}
                    required
                    inputMode="numeric" maxLength={9}
                    value={display.bik ?? ''}
                    onChange={(e) =>
                      setForm({ ...form, bik: digitsOnly(e.target.value) || null })
                    }
                    error={validateBik(display.bik ?? '') ?? undefined}
                  />
                  <Input
                    label={t('companies.field.bankAccount')}
                    required
                    inputMode="numeric" maxLength={20}
                    value={display.bank_account ?? ''}
                    onChange={(e) =>
                      setForm({ ...form, bank_account: digitsOnly(e.target.value) || null })
                    }
                    error={validateBankAccount(display.bank_account ?? '') ?? undefined}
                  />
                  <Input
                    label={t('companies.field.corrAccountFull')}
                    required
                    inputMode="numeric" maxLength={20}
                    value={display.correspondent_account ?? ''}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        correspondent_account: digitsOnly(e.target.value) || null,
                      })
                    }
                    error={validateCorrAccount(display.correspondent_account ?? '') ?? undefined}
                  />
                </div>
              </div>
            ) : (
              <div className="grid gap-4 max-[639px]:grid-cols-1 min-[640px]:grid-cols-2 min-[640px]:[grid-template-columns:1fr_1fr]">
                <ReadOnlyField label={t('companies.field.kpp')} borderRight>
                  {display.kpp ?? '—'}
                </ReadOnlyField>
                <ReadOnlyField label={t('companies.field.ogrn')}>{display.ogrn ?? '—'}</ReadOnlyField>
                <ReadOnlyField label={t('companies.field.bank')} borderRight>
                  {display.bank_name ?? '—'}
                </ReadOnlyField>
                <ReadOnlyField label={t('companies.field.bik')}>{display.bik ?? '—'}</ReadOnlyField>
                <ReadOnlyField label={t('companies.field.bankAccount')} borderRight>
                  {display.bank_account ?? '—'}
                </ReadOnlyField>
                <ReadOnlyField label={t('companies.field.corrAccount')}>
                  {display.correspondent_account ?? '—'}
                </ReadOnlyField>
              </div>
            )}
          </section>

          <section className={card}>
            <h2 className="stryzh-heading-card mb-4">{t('companies.section.signatory')}</h2>
            {editing ? (
              <div className="grid gap-3">
                <Input
                  label={t('companies.field.signatoryName')}
                  required
                  value={display.signatory_name ?? ''}
                  onChange={(e) =>
                    setForm({ ...form, signatory_name: e.target.value || null })
                  }
                />
                <Input
                  label={t('companies.field.signatoryPosition')}
                  required
                  value={display.signatory_position ?? ''}
                  onChange={(e) =>
                    setForm({ ...form, signatory_position: e.target.value || null })
                  }
                />
                <Input
                  label={t('companies.field.signatoryBasis')}
                  required
                  value={display.signatory_basis ?? ''}
                  onChange={(e) =>
                    setForm({ ...form, signatory_basis: e.target.value || null })
                  }
                  hint={t('companies.basisHint')}
                />
              </div>
            ) : (
              <div className="grid gap-4 max-[639px]:grid-cols-1 min-[640px]:grid-cols-2 min-[640px]:[grid-template-columns:1fr_1fr]">
                <ReadOnlyField label={t('companies.field.signatoryName')} borderRight>
                  {display.signatory_name ?? '—'}
                </ReadOnlyField>
                <ReadOnlyField label={t('companies.field.position')}>
                  {display.signatory_position ?? '—'}
                </ReadOnlyField>
                <ReadOnlyField label={t('companies.field.basis')} borderRight>
                  {display.signatory_basis ?? '—'}
                </ReadOnlyField>
                <div className="hidden min-[640px]:block min-[640px]:pl-4" aria-hidden />
              </div>
            )}
          </section>

          <section className={card}>
            <h2 className="stryzh-heading-card mb-4">{t('companies.section.files')}</h2>
            <div className="grid gap-3 max-[639px]:grid-cols-1 min-[640px]:grid-cols-2">
              <DocumentUploader
                label={t('companies.field.facsimile')}
                currentPath={display.facsimile_url ?? null}
                editing={editing}
                accept="image/*"
                getSignedUrl={getCompanyAssetSignedUrl}
                onUpload={(file) => uploadCompanyAsset(company.id, 'facsimile', file)}
                onPathChange={(path) =>
                  setForm((prev) => ({ ...prev, facsimile_url: path }))
                }
              />
              <DocumentUploader
                label={t('companies.field.seal')}
                currentPath={display.seal_url ?? null}
                editing={editing}
                accept="image/*"
                getSignedUrl={getCompanyAssetSignedUrl}
                onUpload={(file) => uploadCompanyAsset(company.id, 'seal', file)}
                onPathChange={(path) =>
                  setForm((prev) => ({ ...prev, seal_url: path }))
                }
              />
            </div>
          </section>

          <section className={card}>
            <CompanyDocumentsSection
              companyId={company.id}
              canUpload={currentRole === 'admin'}
            />
          </section>

          {company.type === 'customer' ? (
            <section className={card}>
              <h2 className="stryzh-heading-card mb-4">{t('companies.section.dkp')}</h2>
              <CompanyDkpTemplateSection
                companyId={company.id}
                templatePath={company.dkp_template_path}
                templateUploadedAt={company.dkp_template_uploaded_at}
                editable={true}
                onChanged={() => {
                  // Любые изменения файла шаблона — атомарные операции,
                  // поэтому просто инвалидируем кэш карточки компании
                  // и списка компаний (там в будущем может быть индикатор).
                  void qc.invalidateQueries({ queryKey: ['company', company.id] })
                  void qc.invalidateQueries({ queryKey: ['companies'] })
                }}
              />
            </section>
          ) : null}

          <section className={card}>
            <h2 className="stryzh-heading-card mb-4">{t('companies.section.users')}</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="text-[var(--stryzh-color-text-secondary)]">
                    <th className="py-2">{t('companies.usersCol.name')}</th>
                    <th className="py-2">{t('companies.usersCol.role')}</th>
                  </tr>
                </thead>
                <tbody>
                  {users.length === 0 ? (
                    <tr>
                      <td colSpan={2} className={emptyTableCellClassName}>
                        {t('common.noData')}
                      </td>
                    </tr>
                  ) : (
                    users.map((u) => (
                      <tr
                        key={u.id}
                        className="cursor-pointer border-t border-[var(--stryzh-color-border-default)] hover:bg-[color:var(--stryzh-color-border-subtle)]"
                        onClick={() => nav(`/users/${u.id}`)}
                      >
                        <td className="py-2 text-[color:var(--stryzh-color-accent-orange)]">
                          {u.full_name}
                        </td>
                        <td className="py-2">{u.role}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>

        <section
          className={`flex min-h-0 flex-col ${card} min-[768px]:flex-1 min-[768px]:basis-0`}
        >
          <h2 className="stryzh-heading-card mb-4 shrink-0">{t('companies.section.orders')}</h2>
          <div className="min-h-0 min-[768px]:flex-1 min-[768px]:overflow-auto">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="text-[var(--stryzh-color-text-secondary)]">
                    <th className="py-2">{t('companies.ordersCol.car')}</th>
                    <th className="py-2">{t('companies.ordersCol.status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.length === 0 ? (
                    <tr>
                      <td colSpan={2} className={emptyTableCellClassName}>
                        {t('common.noData')}
                      </td>
                    </tr>
                  ) : (
                    orders.map((o) => (
                      <tr
                        key={o.id}
                        className="cursor-pointer border-t border-[var(--stryzh-color-border-default)] hover:bg-[color:var(--stryzh-color-border-subtle)]"
                        onClick={() => nav(`/orders/${o.id}`)}
                      >
                        <td className="py-2">
                          {o.brand} {o.model}
                        </td>
                        <td className="py-2">{statusLabelRu(o.status as OrderStatus)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </div>
    </div>
  )
}
